import { useState, useEffect } from 'react';
import AdBanner from './components/AdBanner';
// ...existing code...
import { ThemeProvider } from './contexts/ThemeContext';
import { ThemeToggle } from './components/ThemeToggle';
import './App.css';
// ...existing code...
import './App.css';
// Socket.IO removed: now using polling only

interface Article {
  id: string;
  title: string;
  summary: string;
  category: string;
  severity: string;
  publishedAt: string;
  url: string;
  source?: string;
  tags?: string[];
  readTime?: number;
  threat_level?: number;
  affected_systems?: string[];
  mitigation_steps?: string[];
  createdAt?: string;
  originalPublishDate?: string;
  fetchedAt?: string;
}


function AppContent() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  // Auto-refresh toggle state
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);

  // Set document title for browser tab
  useEffect(() => {
    document.title = 'SiRa — Security Intelligence, Reports & Awareness';
  }, []);

  // Load cached articles and last update from localStorage on mount (top-level hook)
  useEffect(() => {
    const cached = localStorage.getItem('cyberNewsCache');
    const cachedTime = localStorage.getItem('cyberNewsLastUpdate');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) setArticles(parsed);
      } catch {}
    }
    if (cachedTime) setLastUpdated(cachedTime);
  }, []);

  // Polling: fetch articles every 30 seconds, bust cache, only one interval
  useEffect(() => {
    async function fetchArticles(retries = 3, delay = 3000) {
      const apiUrl =
        window.location.hostname === "localhost"
          ? `http://localhost:3000/api/articles?ts=${Date.now()}`
          : `https://api.gosira.tech/api/articles?ts=${Date.now()}`;
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          const res = await fetch(apiUrl, { cache: "no-store" });
          console.log("Fetch status:", res.status, res.statusText, "URL:", res.url);
          const data = await res.json();
          setArticles(data.articles || data);
          // Save to localStorage cache and last update time
          const articlesToCache = Array.isArray(data.articles) ? data.articles : (Array.isArray(data) ? data : []);
          localStorage.setItem('cyberNewsCache', JSON.stringify(articlesToCache));
          const nowStr = new Date().toLocaleString();
          localStorage.setItem('cyberNewsLastUpdate', nowStr);
          setLastUpdated(nowStr);
          console.log(`Auto-refresh fetched ${articlesToCache.length} articles`);
          return;
        } catch (error) {
          if (attempt < retries) {
            await new Promise(resolve => setTimeout(resolve, delay));
          } else {
            // On failure, show last cached news and last update time
            const cached = localStorage.getItem('cyberNewsCache');
            const cachedTime = localStorage.getItem('cyberNewsLastUpdate');
            if (cached) {
              try {
                const parsed = JSON.parse(cached);
                if (Array.isArray(parsed)) setArticles(parsed);
              } catch {}
            }
            if (cachedTime) setLastUpdated(cachedTime);
            if (error instanceof TypeError) {
              console.error("Network or CORS error:", error.message, error);
            } else {
              console.error("Auto-refresh fetch error:", error);
            }
          }
        }
      }
    }
    fetchArticles();
    const interval = setInterval(fetchArticles, 30000); // 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Filtering logic + sort by published date (newest first)
  useEffect(() => {
    let filtered = articles;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((article: Article) => 
        article.title.toLowerCase().includes(query) ||
        article.summary.toLowerCase().includes(query) ||
        article.category.toLowerCase().includes(query) ||
        (article.source?.toLowerCase().includes(query) ?? false) ||
        (article.tags?.some(tag => tag.toLowerCase().includes(query)) ?? false) ||
        (article.affected_systems?.some(system => system.toLowerCase().includes(query)) ?? false)
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter((article: Article) => {
        // Normalize both sides to lowercase and trim
        const articleCat = (article.category || '').toLowerCase().trim();
        const filterCat = selectedCategory.toLowerCase().trim();
        return articleCat === filterCat;
      });
    }

    if (selectedSeverity !== 'all') {
      filtered = filtered.filter((article: Article) => {
        const articleSev = (article.severity || '').toLowerCase().trim();
        const filterSev = selectedSeverity.toLowerCase().trim();
        return articleSev === filterSev;
      });
    }

    // Sort by published date (newest first)
    filtered = filtered.slice().sort((a: Article, b: Article) => {
      const dateA = new Date(a.publishedAt || a.originalPublishDate || a.fetchedAt || 0);
      const dateB = new Date(b.publishedAt || b.originalPublishDate || b.fetchedAt || 0);
      return dateB.getTime() - dateA.getTime();
    });

    setFilteredArticles(filtered);
  }, [articles, selectedCategory, selectedSeverity, searchQuery]);

  // Remove duplicate auto-refresh interval to prevent resource exhaustion

  const loadNews = async () => {
    try {
      setError(null);
  const fetchUrl =
    window.location.hostname === "localhost"
      ? `http://localhost:3000/api/articles?ts=${Date.now()}`
      : `https://api.gosira.tech/api/articles?ts=${Date.now()}`;
  console.log("Fetching articles from:", fetchUrl);
  const response = await fetch(fetchUrl, {
        method: "GET",
        headers: {
          "Accept": "application/json"
        }
      });
      console.log("Response status:", response.status, response.statusText);
      if (!response.ok) {
        throw new Error(`Fetch failed with status ${response.status}`);
      }
      const data = await response.json();
      if (data.success) {
        const articlesList = data.articles || getDemoArticles();
        console.log("Articles loaded:", articlesList.length);
        const sortedArticles = articlesList.sort((a: Article, b: Article) => {
          const dateA = new Date(a.publishedAt || a.originalPublishDate || a.fetchedAt || 0);
          const dateB = new Date(b.publishedAt || b.originalPublishDate || b.fetchedAt || 0);
          return dateB.getTime() - dateA.getTime();
        });
        setArticles(sortedArticles);
        setLastUpdated(new Date().toLocaleTimeString());
      } else {
        throw new Error(data.message || 'Failed to load articles');
      }
    } catch (error) {
      console.error("Error fetching articles:", error);
      setError(`Backend connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setArticles(getDemoArticles());
      setLastUpdated(new Date().toLocaleTimeString());
    } finally {
      setLoading(false);
    }
  };

  // Socket.IO removed: now using polling only

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  const getActualPublishDate = (article: Article) => {
    const actualDate = article.originalPublishDate || article.publishedAt;
    return new Date(actualDate);
  };

  const formatNewsDate = (article: Article) => {
    const actualDate = getActualPublishDate(article);
    const now = new Date();
    let diffMs = now.getTime() - actualDate.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (isNaN(actualDate.getTime())) {
      return {
        formatted: 'Unknown Date',
        relative: 'Date unavailable',
        iso: new Date().toISOString()
      };
    }

    let relative = '';
    // Clamp negative diffMs to zero and show '0 minutes ago' for future-dated articles
    if (diffMs < 0) {
      diffMs = 0;
    }
    if (diffHours < 1) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      relative = `${diffMinutes} minutes ago`;
    } else if (diffHours < 24) {
      relative = `${diffHours} hours ago`;
    } else if (diffDays < 7) {
      relative = `${diffDays} days ago`;
    } else {
      relative = actualDate.toLocaleDateString();
    }

    return {
      formatted: actualDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }),
      time: actualDate.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      }),
      relative: relative,
      iso: actualDate.toISOString(),
      full: actualDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    };
  };

  const getCategoryIcon = (category: string) => {
    switch (category?.toLowerCase()) {
      case 'data breach': return '🔓';
      case 'malware': return '🦠';
      case 'phishing': return '🎣';
      case 'vulnerability': return '⚠️';
      case 'apt': return '🎯';
      case 'supply chain': return '🔗';
      case 'ransomware': return '🔒';
      case 'ddos': return '🌊';
      case 'social engineering': return '🎭';
      case 'zero day': return '💥';
      default: return '🔒';
    }
  };

  const getSeverityClass = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'severity-critical';
      case 'high': return 'severity-high';
      case 'medium': return 'severity-medium';
      case 'low': return 'severity-low';
      default: return 'severity-medium';
    }
  };

  const getValidUrl = (article: Article): string | null => {
    const { url } = article;
    
    if (!url || url.trim() === '') return null;

    let cleanUrl = url.trim();
    
    if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) {
      try {
        new URL(cleanUrl);
        return cleanUrl;
      } catch {
        return null;
      }
    }

    if (cleanUrl.startsWith('www.')) {
      return `https://${cleanUrl}`;
    }

    if (cleanUrl.includes('.') && !cleanUrl.includes(' ')) {
      const domainRegex = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (domainRegex.test(cleanUrl)) {
        return `https://${cleanUrl}`;
      }
    }

    return null;
  };

  // Remove handleArticleClick, use anchor tag for direct link opening

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedSeverity('all');
  };

  const getDemoArticles = () => {
    return [
      {
        id: 'demo-1',
        title: 'Critical Vulnerability Discovered in Major Software',
        summary: 'A critical vulnerability has been discovered in major software affecting millions of users worldwide.',
        category: 'Vulnerability',
        severity: 'Critical',
        publishedAt: new Date().toISOString(),
        url: 'https://example.com/article1',
        source: 'Cyber News AI',
        tags: ['vulnerability', 'critical', 'software'],
        readTime: 2,
        threat_level: 9,
        affected_systems: ['Windows', 'Linux', 'Web Applications'],
        mitigation_steps: ['Update security patches', 'Monitor network traffic', 'Review access logs']
      },
      {
        id: 'demo-2',
        title: 'New Malware Campaign Targets Financial Institutions',
        summary: 'A new malware campaign is targeting financial institutions worldwide.',
        category: 'Malware',
        severity: 'High',
        publishedAt: new Date().toISOString(),
        url: 'https://example.com/article2',
        source: 'Cyber News AI',
        tags: ['malware', 'financial', 'campaign'],
        readTime: 3,
        threat_level: 8,
        affected_systems: ['Windows', 'MacOS', 'Linux'],
        mitigation_steps: ['Update antivirus', 'Monitor network traffic', 'Review access logs']
      }
    ];
  };

  const safeArticles = Array.isArray(articles) ? articles : [];
  const criticalArticles = safeArticles.filter(a => (a.severity || '').toLowerCase() === 'critical').length;
  // Count articles published today based on user's local date
  const now = new Date();
  const userDateString = now.toLocaleDateString();
  const todayArticles = safeArticles.filter(a => {
    const articleDate = getActualPublishDate(a);
    // Compare local date string of article and user
    return articleDate.toLocaleDateString() === userDateString;
  }).length;

  if (loading && articles.length === 0) {
    // Show last available news and update time from localStorage during loading
    let cachedArticles = [];
    let cachedTime = '';
    try {
      const cached = localStorage.getItem('cyberNewsCache');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) cachedArticles = parsed;
      }
      cachedTime = localStorage.getItem('cyberNewsLastUpdate') || '';
    } catch {}
    return (
      <div className="app">
        <nav style={{width:'100%',background:'#111',padding:'1rem 0 0.5rem 0',display:'flex',justifyContent:'center',gap:'2rem'}}>
          <a href="/" style={{color:'#00ff00',textDecoration:'none',fontWeight:'bold'}}>Home</a>
          <a href="/about.html" style={{color:'#00ff00',textDecoration:'none'}}>About</a>
          <a href="/contact.html" style={{color:'#00ff00',textDecoration:'none'}}>Contact</a>
        </nav>
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner-ring"></div>
            <div className="spinner-icon">
              <img
                src="/assets/logo.png"
                alt="SiRa Logo"
                style={{ height: 56, width: 56, borderRadius: 8, boxShadow: '0 2px 8px #0002', background: '#fff' }}
                loading="lazy"
              />
            </div>
          </div>
          <h2 className="loading-title cyber-text">Cyber News</h2>
          <p className="loading-subtitle">Loading latest cyber threats...</p>
          <div className="live-indicator">
            Connecting to Neural Network
          </div>
          {cachedTime && (
            <div className="last-update-info">
              <span>🕒 Last update: {cachedTime}</span>
            </div>
          )}
          <div className="fallback-news-info error-card">
            <div className="error-icon">⚠️</div>
            <h3 className="error-title">Showing last available news</h3>
            <p className="error-message">Backend is loading. Displaying the most recent news until new data arrives.</p>
            <div className="articles-grid">
              {cachedArticles.map((article, idx) => (
                <div key={article.id || idx} className="article-card fallback-card">
                  <div className="article-header">
                    <span className="category-icon">{getCategoryIcon(article.category)}</span>
                    <span>{article.category}</span>
                    <span className={`severity-badge ${getSeverityClass(article.severity)}`}>{article.severity}</span>
                  </div>
                  <h3 className="article-title">{article.title}</h3>
                  <p className="article-summary">{article.summary}</p>
                  <div className="article-meta">
                    <span>Source: {article.source}</span>
                    <span>Published: {new Date(article.publishedAt).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app">
        <nav style={{width:'100%',background:'#111',padding:'1rem 0 0.5rem 0',display:'flex',justifyContent:'center',gap:'2rem'}}>
          <a href="/" style={{color:'#00ff00',textDecoration:'none',fontWeight:'bold'}}>Home</a>
          <a href="/about.html" style={{color:'#00ff00',textDecoration:'none'}}>About</a>
          <a href="/contact.html" style={{color:'#00ff00',textDecoration:'none'}}>Contact</a>
        </nav>
        <div className="error-container">
          <div className="error-card">
            <div className="error-icon">🚨</div>
            <h2 className="error-title">Neural Network Disconnected</h2>
            <p className="error-message">{error}</p>
            <div className="error-details">
              <p>🔗 Backend URL: http://localhost:8003/api/articles</p>
              <p>🕒 Last updated: {lastUpdated || 'Never'}</p>
              <p>🔄 Auto-refresh: {autoRefresh ? 'Every 10 seconds' : 'Paused'}</p>
            </div>
            <button onClick={loadNews} className="retry-button">
              🔄 Reconnect Neural Link
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <nav style={{width:'100%',background:'#111',padding:'1rem 0 0.5rem 0',display:'flex',justifyContent:'center',gap:'2rem'}}>
        <a href="/" style={{color:'#00ff00',textDecoration:'none',fontWeight:'bold'}}>Home</a>
        <a href="/about.html" style={{color:'#00ff00',textDecoration:'none'}}>About</a>
        <a href="/contact.html" style={{color:'#00ff00',textDecoration:'none'}}>Contact</a>
      </nav>
      <header className="app-header">
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', margin: '1rem 0' }}>
          <AdBanner adSlot="8611844882" />
        </div>
        <div className="header-content">
          <div className="logo-section" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <a href="https://gosira.tech" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }} title="">
              <img
                src="/assets/logo.png"
                alt="SiRa Logo"
                style={{ height: 90, width: 90, borderRadius: 8, boxShadow: '0 2px 8px #0002', background: '#fff' }}
                loading="lazy"
              />
              <div className="logo-text">
                <h6 className="logo-title cyber-text" style={{ marginBottom: 0 }}>SiRa — Security Intelligence, Reports & Awareness</h6>
                <div className="logo-subtitle">Real-time Cybersecurity Intelligence</div>
                <div style={{ fontSize: 14, color: '#0078d4', fontWeight: 500, marginTop: 2 }}>gosira.tech</div>
              </div>
            </a>
          </div>
          <div className="header-controls">
            <button 
              className={`auto-refresh-toggle ${autoRefresh ? 'active' : 'paused'}`}
              onClick={toggleAutoRefresh}
            >
              {autoRefresh ? '⏸️ Pause Updates' : '▶️ Resume Updates'}
            </button>
            <div className={`live-indicator ${autoRefresh ? 'active' : 'paused'}`}>
              {autoRefresh ? 'Auto-Update: 10sec' : 'Updates Paused'}
            </div>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <div className="search-section">
        <div className="search-container">
          <div className="search-input-wrapper">
            <div className="search-icon">🔍</div>
            <input
              type="text"
              className="search-input"
              placeholder="Search threats, malware, vulnerabilities, sources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button 
                className="clear-search-button"
                onClick={() => setSearchQuery('')}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          {(searchQuery || selectedCategory !== 'all' || selectedSeverity !== 'all') && (
            <button 
              className="clear-all-button"
              onClick={clearAllFilters}
              title="Clear all filters"
            >
              🗑️ Clear All
            </button>
          )}
        </div>
        {searchQuery && (
          <div className="search-results-info">
            <span className="results-count">
              {filteredArticles.length} result{filteredArticles.length !== 1 ? 's' : ''} found for "{searchQuery}"
            </span>
          </div>
        )}
      </div>

      <div className="stats-bar">
        <div className="stats-container">
          <div className="stat-card">
            <span className="stat-number">{articles.length}</span>
            <span className="stat-label">Total Threats</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{criticalArticles}</span>
            <span className="stat-label">Critical Alerts</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{todayArticles}</span>
            <span className="stat-label">Published Today</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{autoRefresh ? '10sec' : 'OFF'}</span>
            <span className="stat-label">Auto-Update</span>
          </div>
        </div>
      </div>

      <main className="main-content">
        <div className="content-header" style={{marginBottom:'1.5rem'}}>
          <h2 className="content-title cyber-text" style={{margin:0, textAlign:'center'}}>
            🚨 Latest Cyber Threats ({filteredArticles.length})
          </h2>
          <p className="content-subtitle" style={{margin:0, textAlign:'center'}}>
            Real-time intelligence from global threat networks • Click any article to read full details
            {!autoRefresh && <span className="paused-notice"> • Auto-updates paused for reading</span>}
          </p>
        </div>
        {/* About/Contact buttons above filter-controls */}
        <div style={{width:'100%',display:'flex',justifyContent:'center',gap:'2rem',margin:'0 0 2.5rem 0'}}>
          <a href="/about.html" className="contact-btn" style={{fontSize:'1.2rem',padding:'1rem 2.5rem',minWidth:'180px',textAlign:'center',background:'#0088ff',color:'#fff',borderRadius:'2rem',boxShadow:'0 2px 12px #0006',fontWeight:'bold'}}>About Us</a>
          <a href="/contact.html" className="contact-btn" style={{fontSize:'1.2rem',padding:'1rem 2.5rem',minWidth:'180px',textAlign:'center',background:'#00ff00',color:'#111',borderRadius:'2rem',boxShadow:'0 2px 12px #0006',fontWeight:'bold'}}>Contact</a>
        </div>

        <div className="filter-controls">
          <div className="filter-section">
            <label className="filter-label">🏷️ Category:</label>
            <div className="filter-buttons">
              <button 
                className={`filter-button ${selectedCategory === 'all' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('all')}
              >
                🔍 All
              </button>
              <button 
                className={`filter-button ${selectedCategory === 'data breach' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('data breach')}
              >
                🔓 Data Breach
              </button>
              <button 
                className={`filter-button ${selectedCategory === 'apt' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('apt')}
              >
                🎯 APT
              </button>
              <button 
                className={`filter-button ${selectedCategory === 'malware' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('malware')}
              >
                🦠 Malware
              </button>
              <button 
                className={`filter-button ${selectedCategory === 'vulnerability' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('vulnerability')}
              >
                ⚠️ Vulnerability
              </button>
              <button 
                className={`filter-button ${selectedCategory === 'phishing' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('phishing')}
              >
                🎣 Phishing
              </button>
              <button 
                className={`filter-button ${selectedCategory === 'supply chain' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('supply chain')}
              >
                🔗 Supply Chain
              </button>
            </div>
          </div>

          <div className="filter-section">
            <label className="filter-label">⚠️ Severity:</label>
            <div className="filter-buttons">
              <button 
                className={`filter-button ${selectedSeverity === 'all' ? 'active' : ''}`}
                onClick={() => setSelectedSeverity('all')}
              >
                🔍 All
              </button>
              <button 
                className={`filter-button severity-critical ${selectedSeverity === 'critical' ? 'active' : ''}`}
                onClick={() => setSelectedSeverity('critical')}
              >
                🚨 Critical
              </button>
              <button 
                className={`filter-button severity-high ${selectedSeverity === 'high' ? 'active' : ''}`}
                onClick={() => setSelectedSeverity('high')}
              >
                ⚠️ High
              </button>
              <button 
                className={`filter-button severity-medium ${selectedSeverity === 'medium' ? 'active' : ''}`}
                onClick={() => setSelectedSeverity('medium')}
              >
                🟡 Medium
              </button>
              <button 
                className={`filter-button severity-low ${selectedSeverity === 'low' ? 'active' : ''}`}
                onClick={() => setSelectedSeverity('low')}
              >
                🟢 Low
              </button>
            </div>
              <div className="severity-explanations">
                <div><strong>All:</strong> Show every threat, regardless of severity.</div>
                <div><strong>Critical:</strong> Major incidents, urgent vulnerabilities, or attacks with severe impact.</div>
                <div><strong>High:</strong> Serious threats, exploits, or malware with significant risk.</div>
                <div><strong>Medium:</strong> Moderate risks, less urgent vulnerabilities, or ongoing campaigns.</div>
                <div><strong>Low:</strong> Minor issues, informational alerts, or resolved threats.</div>
              </div>
          </div> 
        </div>

        {filteredArticles.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h3 className="empty-title cyber-text">No Threats Found</h3>
            <p className="empty-subtitle">
              {searchQuery || selectedCategory !== 'all' || selectedSeverity !== 'all' 
                ? 'Try adjusting your search or filters to see more results.'
                : 'No threats detected yet. The neural network is actively monitoring global cyber activity.'
              }
            </p>
          </div>
        ) : (
          <div className="articles-grid">
            {filteredArticles.map((article, idx) => {
              const dateInfo = formatNewsDate(article);
              const validUrl = getValidUrl(article);
              return (
                <a
                  key={`${article.id}-${idx}`}
                  className="article-card clickable-card"
                  href={validUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open source link in new tab"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div className="article-header">
                    <div className="article-category">
                      <span className="category-icon">
                        {getCategoryIcon(article.category)}
                      </span>
                      <span>{article.category}</span>
                    </div>
                    <span className={`severity-badge ${getSeverityClass(article.severity)}`}>
                      {article.severity}
                    </span>
                  </div>
                  <h3 className="article-title">{article.title}</h3>
                  <p className="article-summary">
                    {(!article.summary || article.summary.trim().toLowerCase() === 'comments...' || article.summary.trim().toLowerCase() === 'comments')
                      ? 'No comments available'
                      : article.summary}
                  </p>
                  <div className="article-meta">
                    <div className="meta-row">
                      <span>📅 Originally Published:</span>
                      <span title={dateInfo.full}>{dateInfo.formatted}</span>
                    </div>
                    <div className="meta-row">
                      <span>🕒 Time:</span>
                      <span>{dateInfo.time}</span>
                    </div>
                    <div className="meta-row">
                      <span>⏰ Age:</span>
                      <span className="relative-time">{dateInfo.relative}</span>
                    </div>
                    {article.source && (
                      <div className="meta-row">
                        <span>📰 Source:</span>
                        <span>{article.source}</span>
                      </div>
                    )}
                    {article.threat_level && (
                      <div className="meta-row">
                        <span>🎯 Threat Level:</span>
                        <span>{article.threat_level}/10</span>
                      </div>
                    )}
                  </div>
                  <div className="article-actions">
                    <span className="action-button primary-action" title="Read full article">
                      📖 Read Full Article
                    </span>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
